import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, CheckCircle, AlertCircle, Clock, User, List, Table as TableIcon, LayoutGrid, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useComplianceTasks, useDepartments } from "@/hooks";
import { useAuth } from "@/contexts/AuthContext";
import { formatFramework, useCompliancePrograms } from "@/hooks/use-compliance-programs";
import { TaskStatusBadge } from "@/components/compliance/TaskStatusBadge";
import { PriorityIndicator } from "@/components/compliance/PriorityIndicator";
import { TaskFormModal } from "@/components/compliance/TaskFormModal";
import { ImportChecklistTasksModal } from "@/components/compliance/ImportChecklistTasksModal";
import { KanbanBoard } from "@/components/compliance/kanban";
import { TaskDataTable } from "@/components/compliance/TaskDataTable";
import type { ComplianceTask, TaskFilters } from "@/hooks/use-compliance-tasks";
import { Skeleton } from "@/components/ui/skeleton";

type ViewMode = "list" | "kanban" | "table";

export default function ComplianceTasks() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [programFilter, setProgramFilter] = useState<string>("all");
  const [phaseFilter, setPhaseFilter] = useState<string>("all");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ComplianceTask | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "my">("all");
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  const { departments } = useDepartments();
  const { programs } = useCompliancePrograms();

  const { tasks: allTasks, loading, error, createTask, updateTask } = useComplianceTasks({
    status: viewMode === "kanban" ? "all" : (statusFilter as TaskFilters["status"]),
    priority: priorityFilter as TaskFilters["priority"],
    program: programFilter as TaskFilters["program"],
    phase: phaseFilter as TaskFilters["phase"],
    search: searchTerm || undefined,
  });

  const tasks = useMemo(() => {
    if (activeTab === "my" && profile?.id) {
      return allTasks?.filter((t) => t.primary_assignee_id === profile.id) || [];
    }
    return allTasks || [];
  }, [allTasks, activeTab, profile?.id]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "p0": return "bg-red-50 border-red-200 hover:border-red-300 dark:bg-red-950/30 dark:border-red-900 hover:dark:border-red-800";
      case "p1": return "bg-orange-50 border-orange-200 hover:border-orange-300 dark:bg-orange-950/30 dark:border-orange-900 hover:dark:border-orange-800";
      case "p2": return "bg-yellow-50 border-yellow-200 hover:border-yellow-300 dark:bg-yellow-950/30 dark:border-yellow-900 hover:dark:border-yellow-800";
      case "p3": return "bg-green-50 border-green-200 hover:border-green-300 dark:bg-green-950/30 dark:border-green-900 hover:dark:border-green-800";
      default: return "";
    }
  };

  const getDepartmentColor = (departmentId: string | null) => {
    if (!departmentId) return "#6366f1";
    return departments?.find((d) => d.id === departmentId)?.color || "#6366f1";
  };

  const getDepartmentName = (departmentId: string | null) => {
    if (!departmentId) return "General";
    return departments?.find((d) => d.id === departmentId)?.name || "General";
  };

  const isOverdue = (task: ComplianceTask) => {
    if (!task.due_date || task.status === "complete") return false;
    return new Date(task.due_date) < new Date();
  };

  const getDaysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return null;
    return Math.ceil((new Date(dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  };

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const handleNewTask = () => {
    setEditingTask(null);
    setIsFormModalOpen(true);
  };

  const handleSaveTask = async (taskData: Partial<ComplianceTask>) => {
    if (editingTask) {
      await updateTask(editingTask.id, taskData);
    } else {
      await createTask(taskData);
    }
    setIsFormModalOpen(false);
    setEditingTask(null);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-medium text-foreground">Compliance Tasks</h1>
            <p className="text-muted-foreground">Manage your compliance task list</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 pb-4 border-b last:border-0">
                  <Skeleton variant="shimmer" className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton variant="shimmer" className="h-4 w-3/4" />
                    <Skeleton variant="shimmer" className="h-3 w-1/2" />
                  </div>
                  <Skeleton variant="shimmer" className="h-6 w-20" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-medium tracking-tight">Compliance Tasks</h1>
          <p className="text-muted-foreground">Manage your compliance task list</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsImportModalOpen(true)}>
            <Download className="h-4 w-4 mr-2" />
            Import from Checklist
          </Button>
          <Button onClick={handleNewTask}>
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_150px_120px_220px_170px]">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            {viewMode !== "kanban" && (
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="at_risk">At Risk</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="p0">P0 - Critical</SelectItem>
                <SelectItem value="p1">P1 - High</SelectItem>
                <SelectItem value="p2">P2 - Medium</SelectItem>
                <SelectItem value="p3">P3 - Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={programFilter} onValueChange={setProgramFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs</SelectItem>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {formatFramework(program.framework)} · {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={phaseFilter} onValueChange={setPhaseFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Phase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Phases</SelectItem>
                <SelectItem value="policies">Policies</SelectItem>
                <SelectItem value="controls">Controls</SelectItem>
                <SelectItem value="evidence">Evidence</SelectItem>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="audit_ready">Audit Ready</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tasks with Tabs */}
      <Card>
        <CardHeader>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "all" | "my")}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <TabsList>
                  <TabsTrigger value="all">All Tasks</TabsTrigger>
                  <TabsTrigger value="my">My Tasks</TabsTrigger>
                </TabsList>
                <div className="flex gap-1 border rounded-md p-1">
                  <Button
                    variant={viewMode === "list" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("list")}
                    className="h-8 w-8 p-0"
                    title="List view"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "kanban" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("kanban")}
                    className="h-8 w-8 p-0"
                    title="Kanban view"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "table" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("table")}
                    className="h-8 w-8 p-0"
                    title="Table view"
                  >
                    <TableIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardDescription>
                {tasks?.length || 0} task{tasks?.length !== 1 ? "s" : ""}
              </CardDescription>
            </div>
          </Tabs>
        </CardHeader>
        <CardContent className={viewMode === "kanban" ? "p-0 overflow-hidden" : undefined}>
          {error && (
            <div className="p-4 bg-destructive/10 text-destructive rounded-lg">{error}</div>
          )}

          {!tasks || tasks.length === 0 ? (
            <div className={`text-center py-12 ${viewMode === "kanban" ? "px-6" : ""}`}>
              <p className="text-muted-foreground mb-4">No tasks found</p>
              <Button variant="outline" onClick={handleNewTask}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first task
              </Button>
            </div>
          ) : viewMode === "list" ? (
            <div className="space-y-3">
              <AnimatePresence>
                {tasks.map((task, index) => {
                  const daysUntilDue = getDaysUntilDue(task.due_date);
                  const overdue = isOverdue(task);

                  return (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, delay: index * 0.03, ease: [0.4, 0, 0.2, 1] }}
                      whileHover={{ y: -2 }}
                      className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-shadow ${getPriorityColor(task.priority)}`}
                      onClick={() => navigate(`/compliance/tasks/${task.id}`)}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <CheckCircle
                          className={`h-5 w-5 ${task.status === "complete" ? "text-green-600" : "text-gray-300"}`}
                        />
                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium truncate">{task.title}</h3>
                            <PriorityIndicator priority={task.priority} />
                            <TaskStatusBadge status={task.status} />
                            {task.program && (
                              <Badge variant="secondary" className="text-xs">
                                {formatFramework(task.program.framework)}
                              </Badge>
                            )}
                            {task.program_phase && (
                              <Badge variant="outline" className="text-xs capitalize">
                                {task.program_phase.replace("_", " ")}
                              </Badge>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-muted-foreground truncate">{task.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                            {task.primary_assignee && (
                              <div className="flex items-center gap-1.5">
                                <User className="h-3 w-3" />
                                <span className="font-medium text-foreground">{task.primary_assignee.name}</span>
                              </div>
                            )}
                            <Badge
                              variant="outline"
                              style={{
                                backgroundColor: getDepartmentColor(task.department_id) + "15",
                                borderColor: getDepartmentColor(task.department_id),
                              }}
                            >
                              {getDepartmentName(task.department_id)}
                            </Badge>
                            {task.due_date && (
                              <span className={overdue ? "text-destructive" : ""}>
                                {overdue ? (
                                  <><AlertCircle className="h-3 w-3 inline mr-1" />Overdue</>
                                ) : daysUntilDue !== null && daysUntilDue <= 7 ? (
                                  <><Clock className="h-3 w-3 inline mr-1" />Due in {daysUntilDue} day{daysUntilDue !== 1 ? "s" : ""}</>
                                ) : (
                                  <>Due: {new Date(task.due_date).toLocaleDateString()}</>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        {task.primary_assignee && (
                          <Avatar className="h-10 w-10 border-2 border-primary/20">
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                              {getInitials(task.primary_assignee.name)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          ) : viewMode === "kanban" ? (
            <KanbanBoard
              tasks={tasks}
              updateTask={updateTask}
              onTaskClick={(task) => navigate(`/compliance/tasks/${task.id}`)}
              getDepartmentColor={getDepartmentColor}
              getDepartmentName={getDepartmentName}
            />
          ) : (
            <TaskDataTable
              tasks={tasks}
              searchTerm={searchTerm}
              updateTask={updateTask}
              onRowClick={(task) => navigate(`/compliance/tasks/${task.id}`)}
              getDepartmentColor={getDepartmentColor}
              getDepartmentName={getDepartmentName}
            />
          )}
        </CardContent>
      </Card>

      <TaskFormModal
        open={isFormModalOpen}
        onOpenChange={setIsFormModalOpen}
        task={editingTask}
        onSave={handleSaveTask}
      />

      <ImportChecklistTasksModal
        open={isImportModalOpen}
        onOpenChange={setIsImportModalOpen}
      />
    </div>
  );
}
